import React, { useState } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { updateAutoFundingAmount, getAllTransactions } from '~/redux/actions/stripe-actions.js';
import { getUserData } from '~/redux/actions/auth-actions.js';
import { success, failure } from '~/redux/actions/snackbar-actions.js';

// utils
import { MIN_FUNDING_LIMIT } from '~/utils/constants.js';
import { MESSAGES } from '~/utils/message.js';
import { numberWithComma } from '~/utils/helperFunctions.js';

//MUI Component
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { Box, Input, InputLabel, CircularProgress, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import '../AddFunds/styles.scss';

const EnableFunding = (props) => {
  const { handleClose, open } = props;
  const user = useSelector((state) => state.userReducers?.user);
  const [autoFunds, setAutoFunds] = useState(user?.organization?.minAutoFundingAmount || 50);
  const [loading, setLoading] = useState(false);
  const [autoFundError, setAutoFundError] = useState('');
  const [respError, setRespError] = useState('');
  const dispatch = useDispatch();

  const validateFunding = (value, setErrorCallback) => {
    if (isNaN(value) || value < MIN_FUNDING_LIMIT) {
      setErrorCallback(MESSAGES.ACCOUNT.WALLET.STRIPE.ADD_FUNDING_VALIDATION);
      return false;
    } else {
      setErrorCallback('');
      return true;
    }
  };

  const getAutoFundingAmount = () => parseFloat(autoFunds.toString().replaceAll(',', ''));

  const handleUpdateAutoFunding = async () => {
    const isAutoFundValid = validateFunding(getAutoFundingAmount(), setAutoFundError);

    if (!isAutoFundValid) return;
    setLoading(true);
    const data = await updateAutoFundingAmount(getAutoFundingAmount());
    if (data?.status === 200) {
      handleClose();
      dispatch(getUserData());
      dispatch(
        success(
          MESSAGES.ACCOUNT.WALLET.STRIPE.UPDATE_AUTO_FUNDING_SUCCESS,
          MESSAGES.ACCOUNT.WALLET.STRIPE.AUTO_FUNDING
        )
      );
      dispatch(getAllTransactions(1, 10));
    } else {
      const message = data?.data?.message || MESSAGES.GENERAL_ERROR;
      setRespError(message);
      dispatch(failure(message));
    }
    setLoading(false);
  };

  const handleChangeInput = (value) => {
    if (value.length > 8) return;
    if (typeof value == 'string') {
      let valueToAsign = value.replaceAll('$', '').replaceAll(',', '');
      if (valueToAsign !== '' && !/^-?\d*\.?\d{0,2}$/.test(valueToAsign)) return;
      valueToAsign = numberWithComma(valueToAsign);
      setAutoFunds(valueToAsign);
    } else {
      value = numberWithComma(value);
      setAutoFunds(value);
    }
    if (autoFundError && value >= 50) {
      setAutoFundError('');
    }
  };

  const isAutoFundMinEmpty =
    autoFunds === null ||
    autoFunds === undefined ||
    autoFunds.toString().replaceAll('$', '').replaceAll(',', '').trim() === '';

  return (
    <OlcFormModal
      open={open}
      onClose={handleClose}
      title={MESSAGES.ACCOUNT.WALLET.STRIPE.UPDATE_AUTOMATIC_FUNDING}
      className="fundsModalWrapper enableFundingFormModal"
      footer={
        <>
          <OlcButton variant="secondary" onClick={handleClose} disabled={loading}>
            {MESSAGES.ACCOUNT.WALLET.STRIPE.CANCEL}
          </OlcButton>
          <OlcButton
            variant="primary"
            disabled={loading || isAutoFundMinEmpty}
            onClick={handleUpdateAutoFunding}
            sx={{ maxWidth: '160px !important' }}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              MESSAGES.ACCOUNT.WALLET.STRIPE.UPDATE_AUTO_FUNDING
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="fundsModalContent">
        <Box className="amountInputWrapper">
          <Box>
            <InputLabel className="fundsLabelWallet">
              {MESSAGES.ACCOUNT.WALLET.STRIPE.ENTER_MIN_AUTO_AMOUNT}
            </InputLabel>
            <Input
              className="fundsInputFieldWallet"
              sx={{ mt: '8px' }}
              type="text"
              placeholder="$50"
              value={autoFunds ? `$${autoFunds}` : autoFunds}
              onChange={(e) => handleChangeInput(e.target.value, false, true)}
              endAdornment={
                <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                  {autoFunds && (
                    <IconButton
                      className="cpClearInputBtn"
                      size="small"
                      onClick={() => {
                        setAutoFunds('');
                      }}
                      edge="end"
                      sx={{ padding: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              }
            />
            {autoFundError && <Typography className="errorMessage">{autoFundError}</Typography>}
          </Box>
        </Box>
        {respError && <Typography className="fundsModalContent__error">{respError} </Typography>}
      </Box>
    </OlcFormModal>
  );
};

export default EnableFunding;
